import React, { useState, useEffect, useRef } from 'react';
import {
    createEmptyTree,
    normalizeTree,
    setCurrentNodeId,
    goPreviousNode,
    goNextNode,
    goToRootNode,
    goToEndNode,
    promoteVariationNode,
    deleteNodeAndSubtree,
    setNodeComment,
    setNodeNag,
    exportTreeToPgn
} from '../utils/ChessGameTree';
import toast from 'react-hot-toast';

export default function LichessNotationTree({
    gameTree,
    onUpdateTree,
    onSelectNode,
    role = 'COACH',
    isEmbedded = false
}) {
    const norm = normalizeTree(gameTree);
    const [selectedContextNode, setSelectedContextNode] = useState(null);
    const [commentInput, setCommentInput] = useState('');
    const [showNewGameModal, setShowNewGameModal] = useState(false);
    const containerRef = useRef(null);

    // Keyboard navigation (Left / Right / Home / End)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) return;

            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                handlePrevious();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                handleNext();
            } else if (e.key === 'Home') {
                e.preventDefault();
                handleRoot();
            } else if (e.key === 'End') {
                e.preventDefault();
                handleEnd();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [norm]);

    const handleSelectNode = (nodeId) => {
        const updated = setCurrentNodeId(norm, nodeId);
        if (onUpdateTree) onUpdateTree(updated);
        if (onSelectNode) onSelectNode(updated.nodes[nodeId]);
    };

    const handlePrevious = () => {
        const updated = goPreviousNode(norm);
        if (onUpdateTree) onUpdateTree(updated);
        if (onSelectNode) onSelectNode(updated.nodes[updated.currentNodeId]);
    };

    const handleNext = () => {
        const updated = goNextNode(norm);
        if (onUpdateTree) onUpdateTree(updated);
        if (onSelectNode) onSelectNode(updated.nodes[updated.currentNodeId]);
    };

    const handleRoot = () => {
        const updated = goToRootNode(norm);
        if (onUpdateTree) onUpdateTree(updated);
        if (onSelectNode) onSelectNode(updated.nodes[updated.currentNodeId]);
    };

    const handleEnd = () => {
        const updated = goToEndNode(norm);
        if (onUpdateTree) onUpdateTree(updated);
        if (onSelectNode) onSelectNode(updated.nodes[updated.currentNodeId]);
    };

    const handleNewGame = () => {
        setShowNewGameModal(true);
    };

    const confirmNewGame = () => {
        const freshTree = createEmptyTree();
        if (onUpdateTree) onUpdateTree(freshTree);
        if (onSelectNode) onSelectNode(freshTree.nodes.root);
        toast.success("Fresh game started! ♟️🌱");
        setShowNewGameModal(false);
    };

    const handlePromote = (nodeId) => {
        const updated = promoteVariationNode(norm, nodeId);
        if (onUpdateTree) onUpdateTree(updated);
        toast.success("Variation promoted to Main Line! ⭐");
        setSelectedContextNode(null);
    };

    const handleDelete = (nodeId) => {
        if (nodeId === norm.rootId) return;
        const updated = deleteNodeAndSubtree(norm, nodeId);
        if (onUpdateTree) onUpdateTree(updated);
        toast.success("Move branch deleted! 🗑️");
        setSelectedContextNode(null);
    };

    const handleSaveComment = (nodeId, text) => {
        const updated = setNodeComment(norm, nodeId, text);
        if (onUpdateTree) onUpdateTree(updated);
        toast.success("Comment saved! 💬");
        setSelectedContextNode(null);
    };

    const handleSaveNag = (nodeId, nag) => {
        const updated = setNodeNag(norm, nodeId, nag);
        if (onUpdateTree) onUpdateTree(updated);
        setSelectedContextNode(null);
    };

    const handleCopyPgn = () => {
        const pgn = exportTreeToPgn(norm);
        navigator.clipboard.writeText(pgn);
        toast.success("PGN with Tree & Comments copied to clipboard! 📋");
    };

    const getSanFigurine = (sanStr) => {
        if (!sanStr) return '';
        return sanStr
            .replace(/^K/, '♔')
            .replace(/^Q/, '♕')
            .replace(/^R/, '♖')
            .replace(/^B/, '♗')
            .replace(/^N/, '♘');
    };

    /**
     * Recursive component to render tree nodes in Lichess Study style
     */
    const renderNodeTreeRecursive = (nodeId, isVariation = false) => {
        const node = norm.nodes[nodeId];
        if (!node || !node.children || node.children.length === 0) return null;

        const elements = [];
        let currNodeId = node.children[0]; // Mainline child

        while (currNodeId && norm.nodes[currNodeId]) {
            const currNode = norm.nodes[currNodeId];
            const moveNum = Math.floor(currNode.ply / 2) + (currNode.ply % 2 === 1 ? 1 : 0);
            const isWhite = currNode.ply % 2 === 1;
            const isCurrentActive = currNodeId === norm.currentNodeId;

            const sanText = getSanFigurine(currNode.move ? currNode.move.san : '');
            const showNum = isWhite || currNodeId === node.children[0];
            const moveNumPrefix = showNum ? (isWhite ? `${moveNum}. ` : `${moveNum}... `) : '';

            // Main move badge element
            elements.push(
                <span
                    key={`move_${currNode.id}`}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        margin: '2px 3px',
                        position: 'relative'
                    }}
                >
                    {showNum && (
                        <strong style={{
                            color: isVariation ? '#94a3b8' : 'var(--text-primary)',
                            marginRight: '2px',
                            fontSize: '13px',
                            fontWeight: '700'
                        }}>
                            {moveNumPrefix}
                        </strong>
                    )}

                    <span
                        onClick={() => handleSelectNode(currNode.id)}
                        onContextMenu={(e) => {
                            e.preventDefault();
                            if (role === 'COACH') {
                                setSelectedContextNode(currNode);
                                setCommentInput((currNode.comments && currNode.comments[0]) || '');
                            }
                        }}
                        style={{
                            padding: '3px 8px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: isCurrentActive ? '700' : '600',
                            fontSize: '13px',
                            background: isCurrentActive
                                ? 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)'
                                : (isVariation ? 'rgba(148, 163, 184, 0.12)' : 'transparent'),
                            color: isCurrentActive ? '#ffffff' : (isVariation ? 'var(--text-secondary, #94a3b8)' : 'var(--text-primary)'),
                            transition: 'all 0.15s ease',
                            boxShadow: isCurrentActive ? '0 4px 12px rgba(139, 92, 246, 0.4)' : 'none',
                            border: isCurrentActive ? '1px solid #a78bfa' : '1px solid transparent'
                        }}
                        title={role === 'COACH' ? "Click to play move | Right-click for options" : "Click to view move"}
                    >
                        {sanText}
                        {currNode.nag && (
                            <span style={{
                                fontSize: '12px',
                                fontWeight: '900',
                                color: currNode.nag.includes('!') ? '#34d399' : '#f87171',
                                marginLeft: '3px'
                            }}>
                                {currNode.nag}
                            </span>
                        )}
                    </span>

                    {/* Inline Comment Display */}
                    {currNode.comments && currNode.comments.length > 0 && (
                        <span style={{
                            display: 'inline-block',
                            background: 'rgba(59, 130, 246, 0.12)',
                            color: '#3b82f6',
                            fontSize: '11px',
                            padding: '2px 6px',
                            borderRadius: '6px',
                            marginLeft: '4px',
                            fontStyle: 'italic',
                            fontWeight: '600',
                            border: '1px dashed rgba(59, 130, 246, 0.3)'
                        }}>
                            💬 {currNode.comments[0]}
                        </span>
                    )}
                </span>
            );

            // Variations (children[1..N])
            if (currNode.parentId && norm.nodes[currNode.parentId]) {
                const parentNode = norm.nodes[currNode.parentId];
                if (parentNode.children && parentNode.children.length > 1 && parentNode.children[0] === currNode.id) {
                    // Render side variations stemming from parentNode
                    for (let vIdx = 1; vIdx < parentNode.children.length; vIdx++) {
                        const varRootId = parentNode.children[vIdx];
                        const varRootNode = norm.nodes[varRootId];
                        if (varRootNode) {
                            elements.push(
                                <div
                                    key={`var_${varRootId}`}
                                    style={{
                                        margin: '4px 0 4px 14px',
                                        padding: '4px 8px',
                                        background: 'rgba(139, 92, 246, 0.06)',
                                        borderLeft: '3px solid #8b5cf6',
                                        borderRadius: '0 8px 8px 0',
                                        fontSize: '12px',
                                        lineHeight: '1.8'
                                    }}
                                >
                                    <span style={{ color: '#8b5cf6', fontWeight: 'bold', marginRight: '4px' }}>(</span>
                                    {renderSingleBranch(varRootId)}
                                    <span style={{ color: '#8b5cf6', fontWeight: 'bold', marginLeft: '4px', marginRight: '6px' }}>)</span>
                                </div>
                            );
                        }
                    }
                }
            }

            // Move to next mainline child node
            currNodeId = currNode.children[0];
        }

        return elements;
    };

    /**
     * Helper to render a variation branch recursively
     */
    const renderSingleBranch = (startNodeId) => {
        const elements = [];
        let currId = startNodeId;

        while (currId && norm.nodes[currId]) {
            const currNode = norm.nodes[currId];
            const moveNum = Math.floor(currNode.ply / 2) + (currNode.ply % 2 === 1 ? 1 : 0);
            const isWhite = currNode.ply % 2 === 1;
            const isCurrentActive = currId === norm.currentNodeId;

            const sanText = getSanFigurine(currNode.move ? currNode.move.san : '');
            const showNum = isWhite || currId === startNodeId;
            const moveNumPrefix = showNum ? (isWhite ? `${moveNum}. ` : `${moveNum}... `) : '';

            elements.push(
                <span key={`var_node_${currNode.id}`} style={{ display: 'inline-flex', alignItems: 'center', margin: '1px 2px' }}>
                    {showNum && <strong style={{ color: '#94a3b8', marginRight: '2px', fontSize: '12px' }}>{moveNumPrefix}</strong>}
                    <span
                        onClick={() => handleSelectNode(currNode.id)}
                        onContextMenu={(e) => {
                            e.preventDefault();
                            if (role === 'COACH') {
                                setSelectedContextNode(currNode);
                                setCommentInput((currNode.comments && currNode.comments[0]) || '');
                            }
                        }}
                        style={{
                            padding: '2px 6px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: isCurrentActive ? '700' : '600',
                            fontSize: '12px',
                            background: isCurrentActive ? '#8b5cf6' : 'transparent',
                            color: isCurrentActive ? '#ffffff' : 'var(--text-secondary, #94a3b8)',
                            transition: 'all 0.15s ease',
                            boxShadow: isCurrentActive ? '0 2px 6px rgba(139, 92, 246, 0.4)' : 'none'
                        }}
                        title="Right-click for options (Promote / Comment / Delete)"
                    >
                        {sanText}
                        {currNode.nag && <span style={{ color: currNode.nag.includes('!') ? '#34d399' : '#f87171', marginLeft: '2px', fontWeight: 'bold' }}>{currNode.nag}</span>}
                    </span>

                    {currNode.comments && currNode.comments.length > 0 && (
                        <span style={{ fontSize: '10px', color: '#3b82f6', fontStyle: 'italic', marginLeft: '3px' }}>
                            💬 {currNode.comments[0]}
                        </span>
                    )}
                </span>
            );

            // Handle nested variations inside variation!
            if (currNode.children && currNode.children.length > 1) {
                for (let i = 1; i < currNode.children.length; i++) {
                    const subVarId = currNode.children[i];
                    elements.push(
                        <span key={`nested_${subVarId}`} style={{ color: '#a78bfa', fontSize: '11px', margin: '0 2px' }}>
                            ( {renderSingleBranch(subVarId)} )
                        </span>
                    );
                }
            }

            currId = currNode.children[0];
        }

        return elements;
    };

    const hasMoves = norm.nodes.root.children && norm.nodes.root.children.length > 0;

    // Check if selected context node can be promoted to mainline
    const parentOfContext = selectedContextNode?.parentId ? norm.nodes[selectedContextNode.parentId] : null;
    const canPromoteContext = parentOfContext && parentOfContext.children && parentOfContext.children.indexOf(selectedContextNode.id) > 0;

    return (
        <div
            ref={containerRef}
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                borderRadius: '14px',
                padding: '12px',
                boxSizing: 'border-box',
                boxShadow: 'var(--neu-shadow)',
                position: 'relative'
            }}
        >
            {/* Header Strip: Title & Action Buttons */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: '8px',
                borderBottom: '1px solid rgba(163, 177, 198, 0.25)',
                gap: '8px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold', fontSize: '13px', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: '15px' }}>🌿</span> Notation Tree
                </div>

                {role === 'COACH' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button
                            onClick={handleNewGame}
                            style={{
                                padding: '5px 10px',
                                borderRadius: '8px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '11px',
                                cursor: 'pointer',
                                boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap'
                            }}
                            title="Start a fresh game (resets board & tree)"
                        >
                            🌱 New Game
                        </button>
                        <button
                            onClick={handleCopyPgn}
                            style={{
                                padding: '5px 10px',
                                borderRadius: '8px',
                                border: '1px solid rgba(59, 130, 246, 0.4)',
                                background: 'rgba(59, 130, 246, 0.12)',
                                color: '#3b82f6',
                                fontWeight: 'bold',
                                fontSize: '11px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap'
                            }}
                            title="Export PGN with Full Tree & Comments"
                        >
                            📋 PGN
                        </button>
                    </div>
                )}
            </div>

            {/* Dedicated Playback Navigation Bar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                margin: '8px 0 10px 0',
                background: 'var(--bg-secondary)',
                padding: '4px',
                borderRadius: '10px',
                boxShadow: 'var(--neu-shadow-inset)'
            }}>
                <button onClick={handleRoot} style={playbackBtnStyle} title="First Move (Home / |<)">⏮</button>
                <button onClick={handlePrevious} style={playbackBtnStyle} title="Previous Move (Left Arrow / <)">◀</button>
                <button onClick={handleNext} style={playbackBtnStyle} title="Next Move (Right Arrow / >)">▶</button>
                <button onClick={handleEnd} style={playbackBtnStyle} title="Last Move (End / >|)">⏭</button>
            </div>

            {/* Move Tree Content Area */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                paddingRight: '4px',
                lineHeight: '1.9',
                fontSize: '13px',
                wordBreak: 'break-word'
            }}>
                {!hasMoves ? (
                    <div style={{
                        color: 'var(--text-secondary)',
                        fontSize: '13px',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        padding: '30px 10px'
                    }}>
                        ♟️ Play moves on the board to build the move tree, mainline, and variations!
                    </div>
                ) : (
                    renderNodeTreeRecursive(norm.rootId, false)
                )}
            </div>

            {/* Sleek Theme-Matched Right-Click Context Menu Modal */}
            {selectedContextNode && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '10px',
                        right: '10px',
                        zIndex: 999,
                        background: 'var(--bg-primary, #1e293b)',
                        border: '1.5px solid rgba(139, 92, 246, 0.4)',
                        borderRadius: '16px',
                        padding: '14px',
                        boxShadow: 'var(--panel-shadow, 0 12px 35px rgba(0,0,0,0.4))',
                        color: 'var(--text-primary, #ffffff)',
                        backdropFilter: 'blur(16px)'
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', borderBottom: '1px solid rgba(163, 177, 198, 0.3)', paddingBottom: '6px' }}>
                        <strong style={{ color: '#8b5cf6', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>⚙️</span> Move Options: <span style={{ color: 'var(--text-primary)' }}>{selectedContextNode.move?.san}</span>
                        </strong>
                        <button
                            onClick={() => setSelectedContextNode(null)}
                            style={{ background: 'none', border: 'none', color: 'var(--text-secondary, #94a3b8)', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}
                        >
                            ✕
                        </button>
                    </div>

                    {/* Promote Variation Button in Right Click Menu */}
                    {canPromoteContext && (
                        <div style={{ marginBottom: '10px' }}>
                            <button
                                onClick={() => handlePromote(selectedContextNode.id)}
                                style={{
                                    width: '100%',
                                    padding: '7px 12px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}
                            >
                                ⭐ Promote Variation to Main Line
                            </button>
                        </div>
                    )}

                    {/* NAG Symbols */}
                    <div style={{ marginBottom: '10px' }}>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)', marginBottom: '5px', fontWeight: 'bold' }}>NAG Evaluation Symbol:</div>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                            {['!!', '!', '!?', '?!', '?', '??', '±', '∓', '∞'].map(nag => (
                                <button
                                    key={nag}
                                    onClick={() => handleSaveNag(selectedContextNode.id, nag)}
                                    style={{
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(163, 177, 198, 0.3)',
                                        background: selectedContextNode.nag === nag ? '#8b5cf6' : 'var(--bg-secondary)',
                                        color: selectedContextNode.nag === nag ? 'white' : (nag.includes('!') ? '#10b981' : '#ef4444'),
                                        fontWeight: 'bold',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        boxShadow: 'var(--neu-shadow)'
                                    }}
                                >
                                    {nag}
                                </button>
                            ))}
                            <button
                                onClick={() => handleSaveNag(selectedContextNode.id, null)}
                                style={{
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(163, 177, 198, 0.3)',
                                    background: 'var(--bg-secondary)',
                                    color: 'var(--text-secondary)',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    boxShadow: 'var(--neu-shadow)'
                                }}
                            >
                                Clear
                            </button>
                        </div>
                    </div>

                    {/* Comment Input */}
                    <div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)', marginBottom: '5px', fontWeight: 'bold' }}>Move Note / Comment:</div>
                        <input
                            type="text"
                            value={commentInput}
                            onChange={(e) => setCommentInput(e.target.value)}
                            placeholder="Add study comment..."
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid rgba(163, 177, 198, 0.4)',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                fontSize: '12px',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                            {selectedContextNode.id !== norm.rootId && (
                                <button
                                    onClick={() => handleDelete(selectedContextNode.id)}
                                    style={{
                                        padding: '5px 12px',
                                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)'
                                    }}
                                >
                                    🗑️ Delete Move Branch
                                </button>
                            )}
                            <button
                                onClick={() => handleSaveComment(selectedContextNode.id, commentInput)}
                                style={{
                                    padding: '5px 16px',
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    marginLeft: 'auto',
                                    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)'
                                }}
                            >
                                Save Note
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sleek Theme-Matched New Game Confirmation Modal */}
            {showNewGameModal && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 999999,
                    background: 'rgba(0, 0, 0, 0.45)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }} onClick={() => setShowNewGameModal(false)}>
                    <div style={{
                        background: 'var(--bg-primary, #e0e5ec)',
                        border: '1.5px solid rgba(16, 185, 129, 0.4)',
                        borderRadius: '20px',
                        padding: '22px 26px',
                        maxWidth: '380px',
                        width: '100%',
                        boxShadow: 'var(--panel-shadow, 0 20px 40px rgba(0,0,0,0.35))',
                        color: 'var(--text-primary)',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '14px'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '17px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>🌱</span> Start New Game?
                            </h3>
                            <button onClick={() => setShowNewGameModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}>✕</button>
                        </div>
                        
                        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', fontWeight: '500' }}>
                            Are you sure you want to start a fresh game? This will reset the board position to standard chess setup and clear the current move tree.
                        </p>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                            <button
                                onClick={() => setShowNewGameModal(false)}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(163, 177, 198, 0.4)',
                                    background: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    fontWeight: 'bold',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    boxShadow: 'var(--neu-shadow)'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmNewGame}
                                style={{
                                    padding: '8px 18px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                                }}
                            >
                                Yes, Start Fresh
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const playbackBtnStyle = {
    flex: 1,
    height: '30px',
    borderRadius: '7px',
    border: '1px solid rgba(163, 177, 198, 0.3)',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '12px',
    boxShadow: 'var(--neu-shadow)',
    transition: 'all 0.15s ease'
};
